import { useCallback, useMemo, useState } from 'react'

import type { SeatInfo } from './billing-seat.interface'

export interface UseSeatConfirmReturn {
  confirmQuantity: number | null
  isConfirmOpen: boolean
  handleAddSeatsRequest: (quantity: number) => void
  handleConfirmClose: (open: boolean) => void
  handleConfirmPurchase: (onAddSeats: (quantity: number) => void) => void
}

export const useSeatConfirm = (): UseSeatConfirmReturn => {
  const [confirmQuantity, setConfirmQuantity] = useState<number | null>(null)
  const isConfirmOpen = confirmQuantity !== null

  const handleAddSeatsRequest = useCallback((quantity: number): void => {
    setConfirmQuantity(quantity)
  }, [])

  const handleConfirmClose = useCallback((open: boolean): void => {
    if (!open) setConfirmQuantity(null)
  }, [])

  const handleConfirmPurchase = useCallback(
    (onAddSeats: (quantity: number) => void): void => {
      if (confirmQuantity !== null) {
        onAddSeats(confirmQuantity)
        setConfirmQuantity(null)
      }
    },
    [confirmQuantity]
  )

  return {
    confirmQuantity,
    isConfirmOpen,
    handleAddSeatsRequest,
    handleConfirmClose,
    handleConfirmPurchase,
  }
}

const getRemovableSeatsOptions = (additionalSeats: number): number[] => {
  if (additionalSeats <= 0) return []
  const optionList: number[] = []
  for (let i = 1; i <= additionalSeats && i <= 10; i++) {
    optionList.push(i)
  }
  return optionList
}

export interface UseSeatManagerStateReturn {
  removableOptions: number[]
  canRemoveSeats: boolean
  isDisabled: boolean
}

interface UseSeatManagerStateParams {
  seatInfo: SeatInfo
  isLoading: boolean
  isAddingSeats: boolean
  isRemovingSeats: boolean
}

export const useSeatManagerState = ({
  seatInfo,
  isLoading,
  isAddingSeats,
  isRemovingSeats,
}: UseSeatManagerStateParams): UseSeatManagerStateReturn => {
  const removableOptions = useMemo(
    () => getRemovableSeatsOptions(seatInfo.additionalSeats),
    [seatInfo.additionalSeats]
  )
  const canRemoveSeats = removableOptions.length > 0
  const isDisabled = isLoading || isAddingSeats || isRemovingSeats

  return { removableOptions, canRemoveSeats, isDisabled }
}
